import { useState, type FormEvent } from "react";
import { categories } from "@/mocks/discussion";

const FORM_URL = "https://readdy.ai/api/form/dadvsuptto0ceqqngn1g";

type Status = { type: "idle" | "loading" | "success" | "error"; msg: string };

export default function SubmitForm() {
  const [status, setStatus] = useState<Status>({ type: "idle", msg: "" });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // 读取反垃圾蜜罐字段：若被机器人填充则静默放行
    const trap = form.elements.namedItem("website_alt") as HTMLInputElement | null;
    if (trap && trap.value && trap.value.trim()) {
      setStatus({ type: "success", msg: "投稿已收到，感谢你的分享！我们会在核实后尽快上架。" });
      return;
    }

    setStatus({ type: "loading", msg: "" });

    const fd = new FormData(form);
    if (trap) fd.delete("website_alt");

    const content = String(fd.get("content") || "");
    if (content.trim().length === 0) {
      setStatus({ type: "error", msg: "请填写要爆料的内容描述后再提交。" });
      return;
    }

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        body: new URLSearchParams(fd as unknown as Record<string, string>),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? parsed) as Record<string, unknown> | undefined;
      const code = String(parsed?.code ?? "");
      const serverMsg =
        (typeof meta?.message === "string" ? meta.message : "") ||
        (typeof meta?.detail === "string" ? meta.detail : "") ||
        responseText;

      if (!response.ok || code !== "OK" || /spam/i.test(serverMsg)) {
        setStatus({
          type: "error",
          msg: serverMsg && serverMsg.trim() ? serverMsg : "提交失败，请稍后再试。",
        });
        return;
      }

      setStatus({ type: "success", msg: "投稿已收到！审核通过后，你的话题就会出现在热议广场供全网围观。" });
      form.reset();
    } catch {
      setStatus({ type: "error", msg: "网络出了点小问题，请稍后再试一次。" });
    }
  };

  return (
    <section id="submit" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-[0.85fr_1.15fr]">
        {/* 左侧说明 */}
        <div className="lg:pt-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary-500">我要爆料</p>
          <h2 className="section-title mt-2 text-2xl text-foreground-950 md:text-3xl">有第一手新鲜事？开个话题让全网围观</h2>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600 md:text-[15px]">
            不管是明星八卦、身边趣事还是职场/科技新鲜发现，只要内容真实有趣，都欢迎投稿。
            提交后我们会审核并择优展示，你还能在评论区看到围观群众的真实反应。
          </p>

          <div className="mt-6 space-y-3">
            {[
              { t: "真实，不造谣不传谣", d: "投稿需基于事实，杜绝无依据的恶意爆料。" },
              { t: "有趣，值得大家围观", d: "内容有趣或有共鸣，更容易上热榜。" },
              { t: "友善，尊重每位当事人", d: "涉及他人时请隐去隐私，理性表达。" },
            ].map((r) => (
              <div key={r.t} className="flex items-center gap-3 rounded-2xl bg-background-100 p-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-check-line" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-sm font-bold text-foreground-950">{r.t}</p>
                  <p className="text-[13px] text-foreground-600">{r.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 表单 */}
        <form
          onSubmit={handleSubmit}
          data-readdy-form
          className="rounded-3xl border border-background-200 bg-background-100 p-5 md:p-7"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="category" className="mb-1.5 block text-xs font-medium text-foreground-700">
                话题分类 <span className="text-primary-500">*</span>
              </label>
              <select
                id="category"
                name="category"
                required
                defaultValue={categories[0].name}
                className="w-full rounded-xl border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-primary-300"
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-foreground-700">
                你的邮箱 <span className="text-foreground-400">（选填，用于通知结果）</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                className="w-full rounded-xl border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-primary-300"
              />
            </div>
          </div>

          <div className="mt-4">
            <label htmlFor="topic_title" className="mb-1.5 block text-xs font-medium text-foreground-700">
              话题标题 <span className="text-primary-500">*</span>
            </label>
            <input
              id="topic_title"
              name="topic_title"
              required
              maxLength={60}
              placeholder="一句话概括你想让大家围观的事"
              className="w-full rounded-xl border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-primary-300"
            />
          </div>

          <div className="mt-4">
            <label htmlFor="content" className="mb-1.5 block text-xs font-medium text-foreground-700">
              事件描述 <span className="text-primary-500">*</span>
            </label>
            <textarea
              id="content"
              name="content"
              required
              rows={4}
              maxLength={500}
              placeholder="详细描述事件经过、背景与你的看法，尽量客观真实…"
              className="w-full resize-none rounded-xl border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-primary-300"
            ></textarea>
            <p className="mt-1 text-right text-[11px] text-foreground-400">最多 500 字</p>
          </div>

          {/* 反垃圾蜜罐字段 */}
          <div className="bao-field-trap" aria-hidden="true">
            <label htmlFor="website_alt">网站网址（请勿填写）</label>
            <input
              type="text"
              id="website_alt"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              readOnly
            />
          </div>

          <button
            type="submit"
            disabled={status.type === "loading"}
            className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-primary-600 disabled:opacity-70"
          >
            {status.type === "loading" ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base" aria-hidden="true"></i>
                提交中…
              </>
            ) : (
              <>
                <i className="ri-send-plane-fill text-base" aria-hidden="true"></i>
                提交爆料，等围观
              </>
            )}
          </button>

          {status.type === "success" && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-accent-100 px-4 py-3 text-sm text-accent-900">
              <i className="ri-checkbox-circle-fill mt-0.5" aria-hidden="true"></i>
              <span>{status.msg}</span>
            </div>
          )}
          {status.type === "error" && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-primary-100 px-4 py-3 text-sm text-primary-700">
              <i className="ri-error-warning-fill mt-0.5" aria-hidden="true"></i>
              <span>{status.msg}</span>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}